import { initializeApp } from "firebase/app";
import {
  initializeAppCheck,
  ReCaptchaEnterpriseProvider,
} from "firebase/app-check";
import {
  connectAuthEmulator,
  getAuth,
  signInAnonymously,
} from "firebase/auth";
import {
  connectFirestoreEmulator,
  getFirestore,
  initializeFirestore,
} from "firebase/firestore";
import { consumeBecomeTokenOnce } from "./theaWeb/auth/becomeUser";
import { gaAnonRestoreMissed } from "./theaWeb/lib/gaPixel";

/**
 * Hosts that are BOTH a Firebase authorized domain AND a registered redirect
 * URI on the Google OAuth client (console → APIs & Services → Credentials →
 * the web client → Authorized redirect URIs → `https://<host>/__/auth/handler`).
 *
 * Firebase sends Google `redirect_uri: https://<authDomain>/__/auth/handler`
 * and the browser returns the credential through that origin. With a fixed
 * apex authDomain, a visitor on www.givethea.com got a CROSS-ORIGIN handler,
 * and mobile Safari / Chrome iOS partition storage between it and the app, so
 * the redirect credential never came back — Google's half of the same origin
 * split that broke Apple (THE-579). Serving the handler from the page's own
 * origin removes the partition entirely.
 *
 * A host missing from the OAuth client must NOT appear here: Google would
 * reject the exchange outright with redirect_uri_mismatch, which is louder but
 * no less broken. Register the host FIRST, then add it. Anything unlisted
 * (preview channels, localhost) falls back to the build-time env value, which
 * is exactly today's behaviour.
 */
const AUTH_DOMAIN_HOSTS = ["givethea.com", "www.givethea.com"];

export function resolveAuthDomain(
  host = typeof window !== "undefined" ? window.location.hostname : "",
  fallback = process.env.REACT_APP_FIREBASE_AUTH_DOMAIN
) {
  return AUTH_DOMAIN_HOSTS.includes(host) ? host : fallback;
}

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: resolveAuthDomain(),
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
};

// Dev-only: when REACT_APP_USE_EMULATOR=1 the SDK is rewired at first
// instance creation to talk to the local Firebase emulators
// (firestore:8080, auth:9099, functions:5001 — see thea-serverless
// firebase.json). Strict opt-in — never on in production builds. Use
// `REACT_APP_USE_EMULATOR=1 npm start` to enable.
export const USE_EMULATOR = process.env.REACT_APP_USE_EMULATOR === "1";

let _app = null;
let _auth = null;
let _db = null;
let _appCheck = null;

// App Check (2026-08 launch hardening). The theaWeb callables run paid Gemini
// agent loops behind nothing but anonymous auth, and `signInAnonymously` mints
// uids for free — attestation is what makes uid-farming expensive.
//
// Server side this is a RUNTIME three-state gate, not the SDK's
// `enforce_app_check` decorator flag: thea-serverless
// functions/theaWebCommon/app_check.py reads `app_check_mode`
// (off | monitor | enforce) off featureFlags/securityHardening. It ships in
// `monitor`, which verifies the token and logs the outcome but rejects nobody,
// so the pass rate can be measured on real traffic before anyone is turned
// away. Everything below therefore affects a MEASUREMENT today and a
// REJECTION only after that flag is flipped.
//
// - Site key is a reCAPTCHA ENTERPRISE key (console → App Check → Web). It is
//   a public identifier, injected like the other REACT_APP_* config.
// - Missing key: skip init but scream in prod builds — a silently absent token
//   reads as a hard attestation failure the moment enforcement flips on.
const APPCHECK_DEBUG_STORAGE_KEY = "thea:appCheckDebugToken";

// Read a developer-supplied App Check debug token out of localStorage.
//
// WHY LOCALSTORAGE AND NOT A BUILD-TIME SECRET. Firebase preview channels
// (`<project>--<channel>-<hash>.web.app`) are not subdomains of any domain on
// the reCAPTCHA key's allowlist, so they cannot attest once enforcement is on.
// The tempting fix — put a shared debug token in a GitHub secret and bake it
// into preview builds — is a genuinely bad one: preview URLs are publicly
// reachable, a token in the JS bundle is extractable by anyone who finds one,
// and an App Check debug token bypasses attestation for the whole project.
// That would turn every preview into a permanent public bypass of the exact
// control being added.
//
// So the token is never in the bundle. A developer pastes their OWN registered
// token into localStorage once per browser:
//
//     localStorage.setItem('thea:appCheckDebugToken', '<token>')   // registered
//     localStorage.setItem('thea:appCheckDebugToken', 'true')      // mint a new
//                                                                  // one, then
//                                                                  // register it
//
// Nothing secret ships, and a leaked token is one person's to rotate.
function storedDebugToken() {
  try {
    if (typeof window === "undefined" || !window.localStorage) return null;
    const raw = window.localStorage.getItem(APPCHECK_DEBUG_STORAGE_KEY);
    if (!raw) return null;
    return raw === "true" ? true : raw;
  } catch (err) {
    // Safari Private Browsing and "block all cookies" throw on access.
    return null;
  }
}

function initAppCheckFor(app) {
  if (_appCheck) return _appCheck;

  // EMULATOR: don't attest at all.
  //
  // This replaces the old localhost debug-token dance, which is why local
  // browser testing used to require unsetting REACT_APP_RECAPTCHA_ENTERPRISE_
  // SITE_KEY entirely — a fresh debug token had to be minted and registered in
  // the Firebase console before anything worked, and it is pure ceremony:
  // functions running against the local emulator read a featureFlags doc that
  // does not exist, which resolves to `monitor`, which rejects nobody. There is
  // no local behaviour for attestation to protect.
  //
  // The escape hatch is for the one case that does need it — verifying the
  // `enforce` path end to end before flipping prod. Set
  // REACT_APP_APPCHECK_DEBUG=1 plus a stored debug token here, and write
  // `app_check_mode: "enforce"` into the Firestore emulator's
  // featureFlags/securityHardening.
  const debugOptIn = process.env.REACT_APP_APPCHECK_DEBUG === "1";
  if (USE_EMULATOR && !debugOptIn) return null;

  const siteKey = process.env.REACT_APP_RECAPTCHA_ENTERPRISE_SITE_KEY;
  if (!siteKey) {
    if (process.env.NODE_ENV === "production") {
      // eslint-disable-next-line no-console
      console.error(
        "[firebaseConfig] REACT_APP_RECAPTCHA_ENTERPRISE_SITE_KEY missing — " +
          "App Check NOT initialized; callables will fail once enforcement is on",
      );
    }
    return null;
  }
  try {
    // Must be set before initializeAppCheck. `true` mints a fresh token and
    // prints it to the console; register it once in Firebase console →
    // App Check → Apps → Manage debug tokens. (window === self in browsers,
    // which is where the SDK reads it; this repo's eslint env predates
    // `globalThis` and CRA bans bare `self`.)
    const debugToken = storedDebugToken();
    if (typeof window !== "undefined" && debugToken !== null) {
      window.FIREBASE_APPCHECK_DEBUG_TOKEN = debugToken;
    }
    _appCheck = initializeAppCheck(app, {
      provider: new ReCaptchaEnterpriseProvider(siteKey),
      isTokenAutoRefreshEnabled: true,
    });
  } catch (err) {
    // Never let attestation setup take the app down — enforcement is
    // server-side; a client-side init failure surfaces there as a refusal
    // and here as a loud log, not a white screen.
    // eslint-disable-next-line no-console
    console.error("[firebaseConfig] initializeAppCheck failed:", err);
  }
  return _appCheck;
}

// TEMPORARY DIAGNOSTIC (mobile redirect sign-in loss, 2026-07-13). The SDK
// consumes its pendingRedirect/redirectUser sessionStorage records the moment
// auth initializes — before React mounts — so by the time the redirect-lost
// telemetry fires, the arrival state is unobservable. This module loads ahead
// of any auth init, so a snapshot here captures what the return load actually
// arrived with. Read by AuthGateContext's redirect-lost telemetry; remove both
// together once the bug is closed.
export const BOOT_AUTH_STORAGE_KEYS = (() => {
  try {
    if (typeof window === "undefined" || !window.sessionStorage) return ["<no-sessionStorage>"];
    const keys = [];
    for (let i = 0; i < window.sessionStorage.length; i += 1) {
      const k = window.sessionStorage.key(i);
      if (k && (k.startsWith("firebase:") || k.startsWith("thea:authRedirect"))) keys.push(k);
    }
    return keys;
  } catch (err) {
    return ["<sessionStorage-error>"];
  }
})();

export function getAppInstance() {
  if (!_app) {
    _app = initializeApp(firebaseConfig);
    // Immediately after app init, before any Auth/Firestore/Functions
    // instance exists — every later product SDK picks the attestation up
    // from the app instance automatically.
    initAppCheckFor(_app);
  }
  return _app;
}

export function getAuthInstance() {
  if (!_auth) {
    _auth = getAuth(getAppInstance());
    // DO NOT add a `setPersistence(_auth, browserLocalPersistence)` call here.
    // It was here until THE-496 and it was signing people out of tabs they
    // were still using. `getAuth(app)` already installs the hierarchy
    // `[indexedDBLocalPersistence, browserLocalPersistence,
    // browserSessionPersistence]`, which is what we want; overriding it to
    // localStorage puts every tab in a cross-store tug-of-war:
    //
    //  1. `getAuth()` runs `PersistenceUserManager.create()`, which migrates
    //     the credential INTO IndexedDB (`migrationHierarchy[0]`) and then
    //     DELETES the localStorage copy.
    //  2. That constructor registers the cross-tab change listener on the
    //     persistence it selected — IndexedDB — which polls every 800ms.
    //  3. Our `setPersistence(browserLocal)` then moved the credential back
    //     out of IndexedDB into localStorage — but
    //     `PersistenceUserManager.setPersistence()` does NOT move the
    //     listener. It stayed on IndexedDB (@firebase/auth 1.7.6, verified in
    //     node_modules).
    //
    // So a settled tab READ localStorage while WATCHING IndexedDB. Opening a
    // second tab ran step 1 there, which emptied localStorage; the first tab's
    // IndexedDB poll fired, `AuthImpl._onStorageEvent()` read localStorage,
    // found null, and called `_updateCurrentUser(null)`. The already-open tab
    // signed ITSELF out — and `ensureAuth()` below then minted an anonymous
    // user over the permanent session, so it could not self-heal. Reported by
    // Kate as "auto logging out" after command-clicking several boards open;
    // very likely also the source of the 3-7-anon-mints-per-second bursts
    // noted in the 2026-07-21 comment further down this file.
    //
    // The window is the second tab's `initializeCurrentUser` network reload —
    // 60-120ms on a fast link (mostly under the 800ms poll, hence
    // "intermittent"), 400-600ms throttled, where it reproduced 3/3. See
    // `tests/the496/` for the browser repro.
    //
    // The original reason for the explicit call was diagnostic only: surface a
    // rejection in Safari Private Browsing, where IndexedDB is read-only and
    // the SDK silently falls back to in-memory. The default hierarchy already
    // degrades to localStorage there on its own, and that diagnostic is not
    // worth logging real users out for.
    //
    // History, so this is not "fixed" back: #143 replaced `getAuth` with
    // `initializeAuth(app, {persistence: [...]})` and broke Google/Apple
    // sign-in, because `getAuth` is what registers
    // `browserPopupRedirectResolver`. This change does NOT reintroduce that —
    // `getAuth(app)` stays exactly as it is, we only stop overriding the
    // persistence it chose.
    if (USE_EMULATOR) {
      // disableWarnings silences the dev-only banner; safe because the flag
      // gate already prevents this branch from running in prod builds.
      const authPort = Number(process.env.REACT_APP_AUTH_EMULATOR_PORT || 9099);
      connectAuthEmulator(_auth, `http://127.0.0.1:${authPort}`, { disableWarnings: true });
    }
  }
  return _auth;
}

export function getDbInstance() {
  if (!_db) {
    const app = getAppInstance();
    // experimentalForceLongPolling: Firestore's default WebChannel streaming
    // transport can go silent on mobile Safari / restrictive networks — a
    // listen RPC subscribes but its first snapshot never arrives, hanging the
    // listener ~15-20s before recovery. Surfaces as the multi-screen shimmer
    // hang (board, People, Friends).
    //
    // #144 first tried experimentalAutoDetectLongPolling (detect a stall, then
    // fall back). It shipped and STILL hung on a real device: a 2026-06-08
    // phone repro showed a single recommendation-doc listener stuck at
    // `subscribed-waiting` for 14.7s with auto-detect live — auto-detect picks
    // the transport once at first connection and doesn't re-decide on a
    // back-navigation that resurrects a half-dead channel, so it never catches
    // it. forceLongPolling skips the guess and long-polls every listen from the
    // first byte (each request independent — no shared channel to go silent).
    // Trade-off: slightly chattier on healthy WebChannel/desktop networks;
    // accepted for the mobile-Safari ICP. Verified on the preview channel.
    try {
      _db = initializeFirestore(app, {
        experimentalForceLongPolling: true,
      });
    } catch (err) {
      // initializeFirestore throws if Firestore was already initialized for
      // this app; fall back to the existing instance rather than crash.
      // eslint-disable-next-line no-console
      console.error(
        "[firebaseConfig] initializeFirestore failed; using getFirestore():",
        err,
      );
      _db = getFirestore(app);
    }
    if (USE_EMULATOR) {
      const firestorePort = Number(process.env.REACT_APP_FIRESTORE_EMULATOR_PORT || 8080);
      connectFirestoreEmulator(_db, "127.0.0.1", firestorePort);
    }
  }
  return _db;
}

// Lazy proxies preserve the back-compat shape (`import { auth, db } from ...`)
// without firing `getAuth(app)` at module-load time. Member access routes to
// the real instance, with method binding so `this` resolves correctly inside
// Firebase SDK calls. New code should use FirebaseProvider + useAuth()/useDb()
// instead of importing these directly.
function lazyProxy(getInstance) {
  return new Proxy(
    {},
    {
      get(_target, prop) {
        const inst = getInstance();
        const value = inst[prop];
        return typeof value === "function" ? value.bind(inst) : value;
      },
      set(_target, prop, value) {
        // Firebase SDK lazily assigns internal state (e.g.
        // `firestore._firestoreClient = new FirestoreClient(...)`) on first
        // use. Without a set trap those writes hit the empty Proxy target
        // and the SDK then reads `undefined` back → crash. Regression
        // covered by `firebaseConfig.test.ts`.
        const inst = getInstance();
        inst[prop] = value;
        return true;
      },
      has(_target, prop) {
        return prop in getInstance();
      },
      deleteProperty(_target, prop) {
        return delete getInstance()[prop];
      },
      ownKeys() {
        return Reflect.ownKeys(getInstance());
      },
      getOwnPropertyDescriptor(_target, prop) {
        const desc = Object.getOwnPropertyDescriptor(getInstance(), prop);
        if (desc) desc.configurable = true;
        return desc;
      },
      getPrototypeOf() {
        return Object.getPrototypeOf(getInstance());
      },
    }
  );
}

/**
 * @deprecated In React code prefer `useAuth()` from
 * `theaWeb/firebase/FirebaseContext`. This back-compat proxy routes to the one
 * memoized singleton, so it is NOT itself a split-brain risk — the danger is a
 * bare `getAuth(app)` elsewhere spinning up a second instance that bypasses the
 * init settings here. New code should go through the context to keep one
 * lint-guarded access path. Non-React module code (e.g. firebaseFunctions.ts)
 * that can't use a hook may still import this.
 * @type {import('firebase/auth').Auth}
 */
export const auth = lazyProxy(getAuthInstance);
/**
 * @deprecated In React code prefer `useDb()` from
 * `theaWeb/firebase/FirebaseContext`. See the note on `auth` above.
 * @type {import('firebase/firestore').Firestore}
 */
export const db = lazyProxy(getDbInstance);

// Single-flight gate for the anon fallback below. A board cold boot mounts
// many hooks that each call ensureAuth() in the same tick; without
// coordination, every caller that saw `currentUser === null` fired its OWN
// signInAnonymously — prod auth records show bursts of 3–7 anonymous users
// minted in the same second (2026-07-21 investigation), the last write
// winning persistence. When the boot had ALSO failed to restore an existing
// session (see the restore-miss probe below), that overwrite silently
// orphaned the user's boards. One in-flight sign-in, shared by all callers;
// cleared on settle so a FAILED mint can be retried.
let anonSignInInFlight = null;

// SDK-internal storage names for the persisted auth record. Used ONLY by the
// read-only diagnostic probe below — never to alter auth behavior.
const PERSISTED_AUTH_KEY_PREFIX = "firebase:authUser:";
const AUTH_IDB_NAME = "firebaseLocalStorageDb";
const AUTH_IDB_STORE = "firebaseLocalStorage";

function hasPersistedAuthUserInLocalStorage() {
  try {
    for (let i = 0; i < window.localStorage.length; i += 1) {
      const k = window.localStorage.key(i);
      if (k && k.startsWith(PERSISTED_AUTH_KEY_PREFIX)) return true;
    }
  } catch (e) {
    // localStorage unavailable (private mode) — nothing to report on.
  }
  return false;
}

// IndexedDB is where the SDK's default persistence hierarchy actually keeps the
// credential (see the getAuthInstance note re: THE-496), so a localStorage-only
// probe would now silently never fire and this tripwire would rot.
async function hasPersistedAuthUserInIndexedDb() {
  try {
    if (typeof indexedDB === "undefined" || !indexedDB) return false;
    // Only ever open a database that ALREADY exists. `indexedDB.open(name)`
    // with no version CREATES a missing database at version 1 with no object
    // store — and the SDK opens the same name at version 1, so it would then
    // find its store gone and break auth outright. A probe must not be able to
    // do that. Where `databases()` is unavailable, decline to probe instead.
    if (typeof indexedDB.databases !== "function") return false;
    const dbs = await indexedDB.databases();
    if (!dbs || !dbs.some((d) => d && d.name === AUTH_IDB_NAME)) return false;
    return await new Promise((resolve) => {
      const req = indexedDB.open(AUTH_IDB_NAME);
      req.onerror = () => resolve(false);
      req.onsuccess = () => {
        const db = req.result;
        try {
          if (!db.objectStoreNames.contains(AUTH_IDB_STORE)) {
            db.close();
            resolve(false);
            return;
          }
          const keysReq = db
            .transaction(AUTH_IDB_STORE, "readonly")
            .objectStore(AUTH_IDB_STORE)
            .getAllKeys();
          keysReq.onsuccess = () => {
            db.close();
            resolve(
              (keysReq.result || []).some((k) =>
                String(k).startsWith(PERSISTED_AUTH_KEY_PREFIX),
              ),
            );
          };
          keysReq.onerror = () => {
            db.close();
            resolve(false);
          };
        } catch (e) {
          db.close();
          resolve(false);
        }
      };
    });
  } catch (e) {
    return false;
  }
}

/** Read-only peek: is a persisted Firebase Auth user visibly on record in
 *  EITHER store? localStorage is checked first because it is synchronous and
 *  still holds the record on browsers that fell back to it.
 *
 *  The key format is SDK-internal, so this is only ever safe to use where BOTH
 *  answers are acceptable — never to alter auth behavior itself. Its two
 *  callers qualify:
 *    - the `ensureAuth` restore-miss tripwire below (measure-only), and
 *    - `hasPersistedAuthUser` (exported), which gates whether a top-of-funnel
 *      surface pre-mounts its durable saved-state listeners. A false negative
 *      there just defers the listener to the first Save tap (today's
 *      behavior); a false positive mounts a listener that binds the restored
 *      identity. Neither can mint, because a visible record means
 *      `authStateReady()` has an identity to restore.
 *  Deliberately NOT exported as a general "is the user signed in?" — it
 *  answers a storage question, not an auth one. */
async function hasVisiblePersistedAuthUser() {
  if (hasPersistedAuthUserInLocalStorage()) return true;
  return hasPersistedAuthUserInIndexedDb();
}

/**
 * Does this browser already hold a Firebase Auth record? Resolves WITHOUT
 * touching the Auth SDK — it only reads storage — so a surface can ask "is
 * there anything of this visitor's to load?" before paying for Firebase init.
 * A true cold visitor answers `false` and the caller skips the work entirely.
 *
 * See `hasVisiblePersistedAuthUser` for why reading SDK-internal keys is
 * acceptable here and what it may NOT be used for.
 */
export async function hasPersistedAuthUser() {
  return hasVisiblePersistedAuthUser();
}

function documentNavigationType() {
  try {
    const nav = performance.getEntriesByType("navigation")[0];
    return (nav && nav.type) || "unknown";
  } catch (e) {
    return "unknown";
  }
}

/**
 * Sign in anonymously if not already signed in.
 * Returns the stable uid (persists across page reloads).
 */
export async function ensureAuth() {
  const a = getAuthInstance();
  // Admin impersonation boot (?becomeToken= — theaWeb/auth/becomeUser.ts)
  // runs FIRST so its signInWithCustomToken can never lose a race against the
  // anon fallback below or a persisted-session restore. Single-flight and a
  // no-op without the param, so every ensureAuth caller can afford the await.
  await consumeBecomeTokenOnce(a);
  // Wait for persistence to load before deciding whether to fall back to
  // anon sign-in. Without this, `auth.currentUser` is briefly null on
  // reload while indexedDB rehydrates the persisted user — we'd then
  // race-replace the permanent session with a fresh anon one, and every
  // listener (recommendation doc, giftActivity) would subscribe under the
  // wrong subtree and hang.
  await a.authStateReady();
  if (a.currentUser) {
    return a.currentUser.uid;
  }
  if (!anonSignInInFlight) {
    // The assignment stays SYNCHRONOUS — the probe moved inside the body when
    // it grew an async IndexedDB read. Awaiting it out here would let every
    // concurrent caller past the `if` before the gate was set, reinstating the
    // 3–7-mints-per-tick burst this gate exists to stop.
    anonSignInInFlight = (async () => {
      try {
        // DIAGNOSTIC probe (fires once per actual mint decision, not per
        // caller): if a persisted identity is visibly on record while the SDK's
        // restore came up empty, we are about to overwrite a returning user's
        // identity — the orphaning precondition observed live 2026-07-21, whose
        // most likely cause was the cross-store persistence race fixed in
        // THE-496. Measure-only; it stays as the tripwire for a recurrence.
        if (await hasVisiblePersistedAuthUser()) {
          const method = documentNavigationType();
          gaAnonRestoreMissed({ method });
          // eslint-disable-next-line no-console
          console.warn(
            `[auth] anon mint with a persisted identity present (restore miss, nav=${method})`,
          );
        }
        const cred = await signInAnonymously(a);
        // Force token materialization so Firestore's internal auth listener
        // has the credential before any onSnapshot calls go out.
        await cred.user.getIdToken();
        return cred.user.uid;
      } finally {
        anonSignInInFlight = null;
      }
    })();
  }
  return anonSignInInFlight;
}

/** @type {import('firebase/app').FirebaseApp} */
const appProxy = lazyProxy(getAppInstance);
export default appProxy;
